import { atom, useRecoilState } from 'recoil';
import { useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { MapLayers } from 'features/mapping/mapping.constants';
import { useMapLayersState } from 'state/useMapLayersState/useMapLayersState';
import { useOverlappingFeaturesState } from 'state/useOverlappingFeaturesState';

const LIGHTNING_TOOLS_VISIBLE_ATOM_KEY = 'lightningToolsVisible';

const lightningToolsVisibleAtom = atom<boolean>({
  key: LIGHTNING_TOOLS_VISIBLE_ATOM_KEY,
  default: false,
});

type UseLightningToolsState = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

/** Tracks whether the user has opened the lightning tools card. */
export const useLightningToolsState = (): UseLightningToolsState => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useRecoilState(lightningToolsVisibleAtom);
  const { mapLayers, setMapLayerVisibility } = useMapLayersState();
  const { clearOverlappingFeatures } = useOverlappingFeaturesState();

  // Enable the lightning layer, if necessary
  const open = useCallback(() => {
    if (!mapLayers.includes(MapLayers.LIGHTNING)) {
      enqueueSnackbar(
        t('keyboardShortcuts.confirmation.on', {
          layer: t('map.layers.lightning.title'),
          interpolation: { escapeValue: false },
        }),
        { variant: 'info', autoHideDuration: 1000 },
      );
    }
    setMapLayerVisibility(MapLayers.LIGHTNING, true);
    // Opening the lightning tools dismisses any active stacked-nav footer so it doesn't
    // linger over the tools card.
    clearOverlappingFeatures();
    setIsOpen(true);
  }, [
    mapLayers,
    setMapLayerVisibility,
    clearOverlappingFeatures,
    setIsOpen,
    t,
  ]);

  const close = useCallback(() => setIsOpen(false), [setIsOpen]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  return { isOpen, open, close, toggle };
};
